import type { FC } from 'react';
import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Grid,
  Button,
  InputAdornment,
} from '@mui/material';
import {
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { updateIntake, saveIntakeAsync, selectOnboarding } from '../../../store/onboardingSlice';
import { IntakeData } from '../../../api/marketing';

interface BusinessInfoStepProps {
  onNext: () => void;
}

const businessTypes = [
  { value: 'collection_agency', label: 'Collection Agency' },
  { value: 'law_firm', label: 'Law Firm' },
  { value: 'creditor', label: 'Creditor / Original Creditor' },
  { value: 'healthcare', label: 'Healthcare Provider' },
  { value: 'utility', label: 'Utility Company' },
  { value: 'other', label: 'Other' },
];

const usStates = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
];

const BusinessInfoStep: FC<BusinessInfoStepProps> = ({ onNext }) => {
  const dispatch = useAppDispatch();
  const { intake } = useAppSelector(selectOnboarding);

  const { control, handleSubmit, watch, formState: { errors } } = useForm<Partial<IntakeData>>({
    defaultValues: {
      legalName: intake.legalName || '',
      dbaName: intake.dbaName || '',
      businessType: intake.businessType || undefined,
      taxId: intake.taxId || '',
      addressLine1: intake.addressLine1 || '',
      addressLine2: intake.addressLine2 || '',
      city: intake.city || '',
      state: intake.state || '',
      zipCode: intake.zipCode || '',
      businessPhone: intake.businessPhone || '',
      website: intake.website || '',
    },
  });

  const formValues = watch();

  // Auto-save on change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      dispatch(updateIntake(formValues));
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [formValues, dispatch]);

  const onSubmit = async (data: Partial<IntakeData>) => {
    dispatch(updateIntake(data));
    await dispatch(saveIntakeAsync({ ...intake, ...data, currentStep: 2 }));
    onNext();
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        Business Information
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 4 }}>
        Tell us about your company so we can configure your Debtornator instance.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Controller
            name="legalName"
            control={control}
            rules={{ required: 'Legal name is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Legal Business Name"
                error={!!errors.legalName}
                helperText={errors.legalName?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="dbaName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="DBA / Trade Name (if different)"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="businessType"
            control={control}
            rules={{ required: 'Business type is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                select
                label="Business Type"
                error={!!errors.businessType}
                helperText={errors.businessType?.message}
              >
                {businessTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="taxId"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Federal Tax ID (EIN)"
                placeholder="XX-XXXXXXX"
                helperText="Encrypted and stored securely"
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 2, mt: 2 }}>
            Business Address
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="addressLine1"
            control={control}
            rules={{ required: 'Address is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Street Address"
                error={!!errors.addressLine1}
                helperText={errors.addressLine1?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="addressLine2"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Suite / Floor / Unit (optional)"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={5}>
          <Controller
            name="city"
            control={control}
            rules={{ required: 'City is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="City"
                error={!!errors.city}
                helperText={errors.city?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={6} md={3}>
          <Controller
            name="state"
            control={control}
            rules={{ required: 'State is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                select
                label="State"
                error={!!errors.state}
                helperText={errors.state?.message}
              >
                {usStates.map((state) => (
                  <MenuItem key={state} value={state}>
                    {state}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid item xs={6} md={4}>
          <Controller
            name="zipCode"
            control={control}
            rules={{ required: 'ZIP code is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="ZIP Code"
                error={!!errors.zipCode}
                helperText={errors.zipCode?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="businessPhone"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Business Phone"
                placeholder="(555) 555-5555"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="website"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Website (optional)"
                placeholder="https://www.example.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WebsiteIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          endIcon={<ArrowForwardIcon />}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
};

export default BusinessInfoStep;
